import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../utils/api";
import { ToastContainer } from "../components/Toast";
import { hasPageAccess } from "../utils/permissions";

// Role-based defaults and getEffectivePermissions are in utils/permissions.js

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reportsOpen, setReportsOpen] = useState(false);

  const handleLogout = async () => {
    try { await apiFetch("/auth/logout", { method: "POST" }); } catch (e) {}
    logout();
    navigate("/login");
  };

  const canAccess = (pageName) => hasPageAccess(user, pageName);

  const isReportActive = location.pathname.startsWith('/reports');

  const navItems = [
    { name: "Dashboard",        path: "/" },
    { name: "Vehicles",         path: "/vehicles" },
    { name: "Drivers",          path: "/drivers" },
    { name: "Routes",           path: "/routes" },
    { name: "Students",         path: "/students" },
    { name: "Trips",            path: "/trips" },
    { name: "Return Trips Map", path: "/return-trips-map" },
    { name: "Notifications",    path: "/notifications" },
    { name: "Users",            path: "/users" },
  ];

  const visibleItems = navItems.filter(item => canAccess(item.name));

  const canSeeReports =
    canAccess('Reports - Parents') ||
    canAccess('Reports - Trip Report') ||
    canAccess('Reports - Student Report');

  return (
    <aside className="sidebar">
      <div className="sidebar-brand text-gradient">BusTrack Admin</div>

      <div style={{ padding: "0 12px", margin: "8px 0" }}>
        <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "1px" }}>
          Welcome
        </div>
        <div style={{ fontWeight: "600" }}>{user?.full_name || "Administrator"}</div>
      </div>

      <nav className="sidebar-nav">
        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          >
            {item.name}
          </NavLink>
        ))}

        {canSeeReports && (
          <div style={{ marginTop: "4px" }}>
            <div
              onClick={() => setReportsOpen(!reportsOpen)}
              style={{
                padding: "8px 12px", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                color: isReportActive ? "var(--bg-dark)" : "#ffffff",
                fontSize: "12px", fontWeight: "500", borderRadius: "6px",
                background: isReportActive ? "#ffffff" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              <span>Reports</span>
              <span style={{ fontSize: "10px" }}>{reportsOpen ? "▲" : "▼"}</span>
            </div>

            {reportsOpen && (
              <div style={{ paddingLeft: "8px", marginTop: "4px" }}>
                {canAccess('Reports - Parents') && (
                  <NavLink to="/reports/parents" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} style={{ fontSize: "11px", padding: "6px 12px" }}>
                    Parents
                  </NavLink>
                )}
                {canAccess('Reports - Trip Report') && (
                  <NavLink to="/reports/trips" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} style={{ fontSize: "11px", padding: "6px 12px" }}>
                    Trip Report
                  </NavLink>
                )}
                {canAccess('Reports - Student Report') && (
                  <NavLink to="/reports/students" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} style={{ fontSize: "11px", padding: "6px 12px" }}>
                    Student Report
                  </NavLink>
                )}
              </div>
            )}
          </div>
        )}
      </nav>

      <div style={{ padding: "12px 12px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <button onClick={handleLogout} className="btn-secondary" style={{ width: "100%" }}>
          Logout Session
        </button>
      </div>
    </aside>
  );
};

const DashboardLayout = () => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <div className="admin-layout">
      <Sidebar />
      <main className="main-content">
        <Outlet />
      </main>
      <ToastContainer />
    </div>
  );
};

export default DashboardLayout;
